import { useQuery } from 'react-query';
import { UserTypeEnum, useSession } from '@distribusion/portal-lib/auth';
import { BannerLocationEnum } from '@distribusion/portal-lib/user-settings';
import { message } from 'antd';
import config from 'config';
import axios, { AxiosError } from 'lib/axios';
import { useTranslation } from 'lib/translation';

export interface PartnersPreferences {
  color: string;
  default_logo_url: string;
  second_logo_url: string;
  banner_url: string;
  partner_id: string;
  created_at: string;
  updated_at: string;
  display_communication_banner?: boolean;
  communication_banner_location?: BannerLocationEnum;
  communication_banner_text?: string;
  tab_title?: string;
  tab_favicon_url?: string;
}

const getAllPartnersPreferences = async () => {
  const { data } = await axios.get<PartnersPreferences[]>(
    `${config.partnerManagement.apiUrl}/preferences/`,
  );
  return data;
};

const ALL_PARTNERS_PREFERENCES_QUERY_KEY = 'all-partners-preferences';

interface UseAllPartnersPreferencesProps {
  enabled?: boolean;
}

export const useAllPartnersPreferences = (
  { enabled }: UseAllPartnersPreferencesProps = { enabled: true },
) => {
  const { t } = useTranslation();
  return useQuery<PartnersPreferences[], AxiosError>(
    ALL_PARTNERS_PREFERENCES_QUERY_KEY,
    () => getAllPartnersPreferences(),
    {
      keepPreviousData: true,
      retry: () => {
        // 'onError' will be called for each observer, we need this message to be displayed only once,
        // but there is still an issue, we could display only 2 times minimum if more that one observer is present
        // it is a known but for now.
        message.error(t('settings.errors.failed_to_load_partners_preferences'));
        return false;
      },
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      enabled,
    },
  );
};

export const useFirstPartnerPreferencesIfExist = () => {
  // TODO: Think of proper solution to use default settings for internal users
  const { data: sessionData } = useSession();
  const metadata = sessionData.identity?.metadata_public;
  const isInternal =
    metadata && 'access_type' in metadata && metadata.access_type === UserTypeEnum.INTERNAL;
  const { data, ...query } = useAllPartnersPreferences({
    enabled: !isInternal,
  });

  return { ...query, data: isInternal ? undefined : data && data[0] };
};
